/** @format */

import React, { FC, useState, useEffect } from 'react'
import useFormTable from 'src/hooks/useFormTable'
import TablePage from 'src/components/TablePage'
import {
  API,
  HandleDealPolicy,
  PutDefaultEventPolicy,
  CancelDefaultEventPolicy
} from 'src/api/ApiAlarmServer'
import { MonitorSearch, CollapsibleList, TableAction } from 'src/components'
import { eventListToOptions, EventOptions } from 'src/pages/EventCenter/constants'
import { _$, Button, Icon, Message, Tooltip, Switch, Tag, Dialog } from '@king-design/react'
import { useHistory } from 'react-router'
import { request } from 'src/utils'
import { LabelValueItem } from 'src/utils/interface'
import styled from 'styled-components'
import variable from 'src/static/styles/var'
import { getTemplateList } from 'src/api/ApiAlarmTemplate'
import { useAsyncFn } from 'react-use'
interface OwnProps {
  className?: string
  ref?: any
}
const AlarmEventList: FC<OwnProps> = ({ className }) => {
  const [eventList, setEventList] = useState([])
  const [policyName, setPolicyName] = useState('')
  const [productGroup, setProductGroup] = useState<LabelValueItem<string, number>[]>()
  const [tableGroup, setTableGroup] = useState({
    productName: 999
  })
  const [checkedTable, setCheckedTable] = useState<string[]>([])
  const history = useHistory()

  // 告警模板
  const getTemplateName = (id: number) => {
    if (templateList?.length && id) {
      return templateList?.find(item => item.TemplateID === id)?.TemplateName || ''
    }
    return id
  }

  const [{ value: templateList }, template_fetch] = useAsyncFn(async () => {
    const result = await getTemplateList({
      PageIndex: 1,
      PageSize: 9999,
      ProductType: 999
    })
    return result
  }, [])

  useEffect(() => {
    getEventList()
    template_fetch()
  }, [])

  // 获取所有事件
  const getEventList = async () => {
    const result = await request(API.GetListSysEvents, {
      data: {
        ProductType: 999
      }
    })
    if (result && result?.code === 'Success' && result?.data.eventList) {
      const eventList = result.data.eventList
      setEventList(eventList)
      const filter = eventListToOptions(eventList) as EventOptions[]
      const map = filter?.map(item => ({
        label: item.label,
        value: item.value as number
      }))
      setProductGroup([{ label: _$('全部'), value: 999 }, ...map])
      return result
    } else {
      Message.error(result?.message)
      return null
    }
  }

  // 事件告警列表
  const {
    run,
    result: tableData,
    pageState,
    loading,
    total
  }: any = useFormTable(
    API.GetEventPolicy,
    {
      params: {
        ProductType: tableGroup.productName,
        SearchPolicyName: policyName
      }
    },
    data => ({
      tableData: data?.policyList,
      totalCount: data?.totalCount
    })
  )

  const eventScheme = {
    policyName: {
      title: _$('策略名称'),
      width: 200
    },
    productName: {
      title: _$('产品类型'),
      width: 100,
      group: productGroup,
      template: function (item) {
        return (
          <>
            {item.productName}
            {item.policyType === 1 ? (
              <Tag type="primary" size="mini">
                {_$('默认')}
              </Tag>
            ) : null}
          </>
        )
      }
    },
    eventRules: {
      title: _$('告警规则'),
      template: function (item) {
        const { productType, templateId, eventRules } = item
        const allEvent = eventList.filter(item => item.productType === productType)
        if (eventRules?.length === allEvent?.length) {
          return <p>{_$('全部事件')}</p>
        } else {
          const templateName = getTemplateName(templateId)
          return (
            <ul className="rule-list">
              {templateName ? (
                <li className="text-overflow" style={{ fontWeight: 700 }}>
                  <Tooltip content={_$(templateName)}>
                    <span>
                      {_$('关联模板名称')}：{templateName}
                    </span>
                  </Tooltip>
                </li>
              ) : null}
              <CollapsibleList<{ eventName: string; eventNameDesc: string }>
                data={eventRules}
                renderItem={item => {
                  return <span>{item?.eventNameDesc}</span>
                }}
                getItemKey={item => item.eventName}
              />
            </ul>
          )
        }
      }
    },
    resouceBindType: {
      title: _$('关联实例'),
      width: 100,
      template: function (item) {
        return (
          <span>
            {item.resourceBindType &&
            (item.resourceBindType === 2 || item.resourceBindType === 127) ? (
              <p>
                {_$('项目')}：{item.projectName || ''}
                {item.resourceBindType === 127 ? (
                  <span className="deleted_color">{_$('（已删除）')}</span>
                ) : null}
              </p>
            ) : null}
            <p>
              {_$('实例')}：{item.instanceCount || 0}
            </p>
          </span>
        )
      }
    },
    userNotice: {
      title: _$('告警联系人'),
      width: 100,
      template: function (item) {
        const contactList = item.userNotice
        const group = contactList?.filter(i => i?.contactFlag === 1)
        const user = contactList?.filter(i => i?.contactFlag === 2)
        const schedule = contactList?.filter(i => i?.contactFlag === 3)
        return (
          <span>
            {!contactList || !contactList?.length ? (
              <span>
                <Tooltip content={_$('未配置告警联系人将不会收到告警')}>{_$('未配置')}</Tooltip>
              </span>
            ) : (
              <>
                <span>
                  {_$('联系组')}：{group?.length}
                </span>
                <br />
                <span>
                  {_$('联系人')}：{user?.length}
                </span>
                <br />
                <span>
                  {_$('值班表')}：{schedule?.length}
                </span>
              </>
            )}
          </span>
        )
      }
    },
    enabled: {
      title: _$('状态'),
      width: 100,
      template: function (item) {
        return (
          <Switch
            on={_$('启用')}
            off={_$('禁用')}
            width="62"
            value={item.enabled === 0}
            beforeChange={async (from, to) => {
              if (item.policyType === 1) {
                Message.warning(_$('默认策略不能禁用'))
              } else {
                return await HandleDealEventPolicy(to ? 'enable' : 'disable', [item?.policyName])
              }
            }}
          />
        )
      }
    },
    action: {
      title: _$('操作'),
      width: 220,
      template: function (item) {
        return (
          <TableAction
            actions={[
              {
                key: 'alarmHistory',
                label: _$('告警历史'),
                onClick: () => {
                  history.push(
                    `/monitor/alarmServer/history/detail/event/${encodeURIComponent(
                      item.policyName
                    )}`
                  )
                }
              },
              {
                key: 'modify',
                label: _$('编辑'),
                onClick: () =>
                  history.push(
                    `/monitor/alarmServer/rule/editEvent/${encodeURIComponent(item.policyName)}`
                  )
              },
              {
                key: 'delete',
                label: _$('删除'),
                disabled: item.policyType === 1,
                onClick: () => {
                  let dialog: Dialog
                  Dialog.confirm({
                    content: _$('确认删除当前策略？'),
                    ref: _dialog => (dialog = _dialog)
                  }).then(async () => {
                    dialog.showLoading()
                  })
                }
              },
              {
                key: 'cancelDefaultPolicy',
                label: _$('取消默认策略'),
                hidden: item.policyType === 1,
                onClick: () => {
                  let dialog: Dialog
                  Dialog.confirm({
                    content: _$('确认取消当前默认策略？'),
                    ref: _dialog => (dialog = _dialog)
                  }).then(async () => {
                    dialog.showLoading()
                    await CancelDefaultEventPolicy(item.policyName)
                    dialog.close()
                  })
                }
              },
              {
                key: 'setDefaultPolicy',
                label: _$('设置默认策略'),
                hidden: item.policyType !== 1,
                disabled: item.enabled === 1 || item.resourceBindType === 2,
                onClick: () => {
                  let dialog: Dialog
                  Dialog.confirm({
                    content: _$('确认设置当前默认策略？'),
                    ref: _dialog => (dialog = _dialog)
                  }).then(async () => {
                    dialog.showLoading()
                    await PutDefaultEventPolicy(item.policyName)
                    dialog.close()
                  })
                }
              }
            ]}
          />
        )
      }
    }
  }

  // type: 处理方式
  const HandleDealEventPolicy = async (type: string, policyNames: string[]) => {
    const paramsData = {
      PolicyNames: policyNames
    }
    const resData = await HandleDealPolicy({ method: type, data: paramsData })
    if (resData?.['code'] === 'Success') {
      run()
      if (type === 'delete') {
        Message.success(_$('删除成功'))
      } else if (type === 'disable') {
        Message.success(_$('禁用成功'))
      } else if (type === 'enable') {
        Message.success(_$('启用成功'))
      }
      return true
    } else {
      Message.warning(resData?.['message'])
    }
  }
  // type: 事件处理类型
  // policyName: 事件类型名称
  const delEventPolicy = (type: string) => {
    if (!checkedTable?.length) return
    HandleDealEventPolicy(type, checkedTable)
  }

  return (
    <div className={className}>
      <div className="header">
        <div className="header-left">
          <Button
            type="primary"
            onClick={() => {
              // setCreateAlarmVisible(true)
              history.push(`/monitor/alarmServer/rule/createEvent`)
            }}
          >
            <Icon className="ion-plus-round"></Icon>
            {_$('新建告警策略')}
          </Button>
          <Button disabled={!checkedTable?.length} onClick={() => delEventPolicy('disable')}>
            {_$('禁用')}
          </Button>
          <Button disabled={!checkedTable?.length} onClick={() => delEventPolicy('enable')}>
            {_$('启用')}
          </Button>
          <Button disabled={!checkedTable?.length} onClick={() => delEventPolicy('delete')}>
            {_$('删除')}
          </Button>
        </div>
        <div>
          <MonitorSearch
            value={policyName}
            placeholder={_$('输入告警策略名称')}
            onChange={value => {
              run({
                ProductType: tableGroup.productName,
                SearchPolicyName: value,
                PageIndex: 1
              })
              setPolicyName(value)
            }}
          />
        </div>
      </div>
      {eventScheme ? (
        <TablePage
          tableProps={{
            stickHeader: 50,
            scheme: eventScheme,
            data: tableData,
            rowCheckable: false,
            resizable: true,
            rowKey: value => value.policyName,
            onChangeCheckedKeys: value => {
              setCheckedTable(value)
            },
            group: tableGroup,
            onChangeGroup: value => {
              setTableGroup(value)
              run({
                PageIndex: 1,
                ProductType: value.productName !== '' ? value.productName : 999,
                SearchPolicyName: policyName
              })
            }
          }}
          pageProps={{ total, ...pageState }}
          loading={loading}
          run={run}
        ></TablePage>
      ) : null}
    </div>
  )
}

export default styled(AlarmEventList)`
  .header {
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
    .header-left {
      & > * {
        margin-right: 8px;
      }
    }
  }
  .none-setting {
    cursor: pointer;
    color: ${variable.errorColor};
  }
  .k-table {
    p {
      margin-bottom: 0;
    }
    .k-tag {
      margin-left: 5px;
    }
    .rule-list {
      margin-bottom: 0;
      li {
        max-width: 100%;
        height: 18px;
        span {
          display: inline-block;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }
`
